import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { MessageService } from '../services/messageService';
import { useAuthStore } from './authStore';

export interface DirectMessage {
  id: string;
  message: string;
  messageType: 'TEXT' | 'EMOJI' | 'GIF' | 'IMAGE';
  gifUrl?: string | undefined;
  gifId?: string | undefined;
  sentAt: string;
  senderId: string;
  receiverId: string;
  sender: {
    id: string;
    username: string;
    profilePhoto: string | null;
    suspendedAt?: Date | string | null | undefined;
  };
  receiver?: {
    id: string;
    username: string;
    profilePhoto: string | null;
    suspendedAt?: Date | string | null | undefined;
  };
}

export interface Conversation {
  user: {
    id: string;
    username: string;
    profilePhoto: string | null;
  };
  lastMessage: {
    message: string;
    sentAt: string;
  } | null;
  hasUnread: boolean;
}

export interface MessagingRestriction {
  canMessage: boolean;
  reason: 'ALLOWED' | 'PREVIOUS_CONVERSATION' | 'SHARED_GROUPS' | 'GROUPS_REQUIRED' | 'SAME_USER';
  message?: string;
}

interface MessageState {
  // Conversations state
  conversations: Conversation[];
  isLoadingConversations: boolean;
  
  // Messages state
  messages: Record<string, DirectMessage[]>; // userId -> messages
  isLoadingMessages: Record<string, boolean>; // userId -> loading state
  hasMoreMessages: Record<string, boolean>; // userId -> has more messages
  
  // Current active conversation
  activeConversationUserId: string | null;
  
  // Messaging restrictions state
  messagingRestrictions: Record<string, MessagingRestriction>; // userId -> restriction status
  isCheckingMessagingStatus: Record<string, boolean>; // userId -> checking status
  
  // Global unread state
  hasUnreadMessages: boolean;
}

interface MessageActions {
  // Conversation actions
  setConversations: (conversations: Conversation[]) => void;
  setLoadingConversations: (loading: boolean) => void;
  updateConversation: (userId: string, updates: Partial<Conversation>) => void;
  markConversationAsRead: (userId: string) => void;
  
  // Message actions
  setMessages: (userId: string, messages: DirectMessage[]) => void;
  addMessage: (userId: string, message: DirectMessage) => void;
  prependMessages: (userId: string, messages: DirectMessage[]) => void;
  setLoadingMessages: (userId: string, loading: boolean) => void;
  setHasMoreMessages: (userId: string, hasMore: boolean) => void;
  
  // Active conversation
  setActiveConversation: (userId: string | null) => void;
  
  // Messaging restrictions actions
  setMessagingRestriction: (userId: string, restriction: MessagingRestriction) => void;
  setCheckingMessagingStatus: (userId: string, checking: boolean) => void;
  checkMessagingStatus: (userId: string) => Promise<MessagingRestriction>;
  
  // Global unread state
  setHasUnreadMessages: (hasUnread: boolean) => void;
  checkUnreadMessages: () => Promise<void>;
  
  // Utilities
  clearAllData: () => void;
  getUserMessages: (userId: string) => DirectMessage[];
  loadConversations: () => Promise<void>;
  loadMessages: (userId: string, cursor?: string) => Promise<void>;
  sendMessage: (receiverId: string, message: string, messageType?: 'TEXT' | 'EMOJI' | 'GIF' | 'IMAGE', gifUrl?: string, gifId?: string) => Promise<void>;
}

type MessageStore = MessageState & MessageActions;

const initialState: MessageState = {
  conversations: [],
  isLoadingConversations: false,
  messages: {},
  isLoadingMessages: {},
  hasMoreMessages: {},
  activeConversationUserId: null,
  messagingRestrictions: {},
  isCheckingMessagingStatus: {},
  hasUnreadMessages: false,
};

export const useMessageStore = create<MessageStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // Conversation actions
    setConversations: (conversations) => {
      set({ conversations });
      // Update global unread state
      const hasUnread = conversations.some(conv => conv.hasUnread);
      set({ hasUnreadMessages: hasUnread });
    },

    setLoadingConversations: (loading) => {
      set({ isLoadingConversations: loading });
    },

    updateConversation: (userId, updates) => {
      set((state) => {
        const updatedConversations = state.conversations.map(conv =>
          conv.user.id === userId ? { ...conv, ...updates } : conv
        );
        const hasUnread = updatedConversations.some(conv => conv.hasUnread);
        
        return {
          conversations: updatedConversations,
          hasUnreadMessages: hasUnread
        };
      });
    },

    markConversationAsRead: (userId) => {
      set((state) => {
        const updatedConversations = state.conversations.map(conv =>
          conv.user.id === userId ? { ...conv, hasUnread: false } : conv
        );
        const hasUnread = updatedConversations.some(conv => conv.hasUnread);
        
        return {
          conversations: updatedConversations,
          hasUnreadMessages: hasUnread
        };
      });
    },

    // Message actions
    setMessages: (userId, messages) => {
      set((state) => ({
        messages: {
          ...state.messages,
          [userId]: messages
        }
      }));
    },

    addMessage: (userId, message) => {
      const { user } = useAuthStore.getState();
      const currentUserId = user?.id?.toString();
      
      // More robust check for own message - check both sender and receiver
      const isOwnMessage = message.senderId === currentUserId;
      
      const { activeConversationUserId } = get();

      // Check if this message is for the currently active conversation
      const isActiveConversation = activeConversationUserId === userId;

      // Skip duplicates (e.g. message already added optimistically by
      // the send flow, then received again via socket)
      const existingMessages = get().messages[userId] || [];
      if (message.id && existingMessages.some(m => m.id === message.id)) {
        return;
      }

      set((state) => ({
        messages: {
          ...state.messages,
          [userId]: [...(state.messages[userId] || []), message]
        }
      }));

      // Check if conversation exists, if not create it
      const { conversations } = get();
      const existingConversation = conversations.find(conv => conv.user.id === userId);
      
      if (existingConversation) {
        // Update existing conversation
        get().updateConversation(userId, {
          lastMessage: {
            message: message.message,
            sentAt: message.sentAt
          },
          // Mark as unread ONLY if:
          // 1. It's not our own message AND
          // 2. It's not the currently active conversation (user is not actively viewing it)
          hasUnread: !isOwnMessage && !isActiveConversation
        });
      } else {
        // Create new conversation for new user; for own messages (sent from
        // another tab) the conversation partner is the receiver, not the sender
        const otherUser = isOwnMessage && message.receiver ? message.receiver : message.sender;
        const newConversation = {
          user: {
            id: userId,
            username: otherUser.username,
            profilePhoto: otherUser.profilePhoto
          },
          lastMessage: {
            message: message.message,
            sentAt: message.sentAt
          },
          // Mark as unread ONLY if it's not our own message AND not the active conversation
          hasUnread: !isOwnMessage && !isActiveConversation
        };
        
        set((state) => ({
          conversations: [newConversation, ...state.conversations]
        }));
      }
      
      // Update global unread state if this is not our own message and not active conversation
      if (!isOwnMessage && !isActiveConversation) {
        const updatedConversations = get().conversations;
        const hasUnread = updatedConversations.some(conv => conv.hasUnread);
        set({ hasUnreadMessages: hasUnread });
      }
      
      // If this IS the active conversation and it's not our message, mark it as read immediately
      if (!isOwnMessage && isActiveConversation) {
        // Small delay to ensure the conversation update is complete
        setTimeout(() => {
          get().markConversationAsRead(userId);
        }, 50);
      }
    },

    prependMessages: (userId, messages) => {
      set((state) => ({
        messages: {
          ...state.messages,
          [userId]: [...messages, ...(state.messages[userId] || [])]
        }
      }));
    },

    setLoadingMessages: (userId, loading) => {
      set((state) => ({
        isLoadingMessages: {
          ...state.isLoadingMessages,
          [userId]: loading
        }
      }));
    },

    setHasMoreMessages: (userId, hasMore) => {
      set((state) => ({
        hasMoreMessages: {
          ...state.hasMoreMessages,
          [userId]: hasMore
        }
      }));
    },

    // Active conversation
    setActiveConversation: (userId) => {
      set({ activeConversationUserId: userId });
      
      // Mark as read when opening conversation
      if (userId) {
        get().markConversationAsRead(userId);
        
        // Also call API to mark as read
        MessageService.markConversationAsRead(userId).catch(error => {
          console.error('Failed to mark conversation as read:', error);
        });
      }
    },

    // Messaging restrictions actions
    setMessagingRestriction: (userId, restriction) => {
      set((state) => ({
        messagingRestrictions: {
          ...state.messagingRestrictions,
          [userId]: restriction
        }
      }));
    },

    setCheckingMessagingStatus: (userId, checking) => {
      set((state) => ({
        isCheckingMessagingStatus: {
          ...state.isCheckingMessagingStatus,
          [userId]: checking
        }
      }));
    },

    checkMessagingStatus: async (userId) => {
      const { setCheckingMessagingStatus, setMessagingRestriction } = get();
      
      setCheckingMessagingStatus(userId, true);
      
      try {
        const response = await MessageService.checkMessagePermission(userId);
        const restriction: MessagingRestriction = {
          canMessage: response.canMessage,
          reason: response.reason,
          ...(response.message && { message: response.message })
        };
        
        setMessagingRestriction(userId, restriction);
        return restriction;
      } catch (error) {
        console.error('Failed to check messaging status:', error);
        const fallbackRestriction: MessagingRestriction = {
          canMessage: false,
          reason: 'GROUPS_REQUIRED',
          message: 'Could not verify messaging status'
        };
        setMessagingRestriction(userId, fallbackRestriction);
        return fallbackRestriction;
      } finally {
        setCheckingMessagingStatus(userId, false);
      }
    },

    // Global unread state
    setHasUnreadMessages: (hasUnread) => {
      set({ hasUnreadMessages: hasUnread });
    },

    checkUnreadMessages: async () => {
      try {
        const response = await MessageService.hasUnreadMessages();
        set({ hasUnreadMessages: response.hasUnread });
      } catch (error) {
        console.error('Failed to check unread messages:', error);
      }
    },

    // Utilities
    clearAllData: () => {
      set({
        ...initialState,
        messagingRestrictions: {},
        isCheckingMessagingStatus: {}
      });
    },

    getUserMessages: (userId) => {
      return get().messages[userId] || [];
    },

    loadConversations: async () => {
      try {
        set({ isLoadingConversations: true });
        const response = await MessageService.getConversations();
        get().setConversations(response.conversations);
      } catch (error) {
        console.error('Failed to load conversations:', error);
      } finally {
        set({ isLoadingConversations: false });
      }
    },

    loadMessages: async (userId, cursor) => {
      try {
        get().setLoadingMessages(userId, true);
        const response = await MessageService.getConversationMessages(userId, 50, cursor);
        
        if (cursor) {
          // Loading more messages (prepend)
          get().prependMessages(userId, response.messages);
        } else {
          // Initial load (replace)
          get().setMessages(userId, response.messages);
        }
        
        get().setHasMoreMessages(userId, response.hasNextPage);
      } catch (error) {
        console.error('Failed to load messages:', error);
      } finally {
        get().setLoadingMessages(userId, false);
      }
    },

    sendMessage: async (receiverId, message, messageType = 'TEXT', gifUrl, gifId) => {
      try {
        const response = await MessageService.sendMessage({
          receiverId: parseInt(receiverId),
          message,
          messageType,
          ...(gifUrl && { gifUrl }),
          ...(gifId && { gifId })
        });
        
        // Add message to store (this is our own sent message, never mark as unread)
        const sentMessage = {
          id: response.id,
          message: response.message,
          messageType: response.messageType,
          gifUrl: response.gifUrl,
          gifId: response.gifId,
          sentAt: response.sentAt,
          senderId: response.sender.id,
          receiverId: response.receiver.id,
          sender: response.sender
        };
        
        
        get().addMessage(receiverId, sentMessage);
        
        // Note: We don't need to reload conversations here because addMessage already updates
        // the conversation with the new message and manages unread state properly.
        // Calling loadConversations() could overwrite our local state with stale API data.
      } catch (error) {
        throw error;
      }
    }
  }))
);